import {
  VStack,
  Box,
  Switch,
  Input,
  HStack,
  Spacer,
  Text,
  Image,
  Checkbox,
  Select,
} from '@chakra-ui/react'
import { FunctionComponent, useEffect, useState } from 'react'
import { textures as txs } from '../../../../_mocks_/textures'
import { Shop } from '../../../type/backendType'
import { IMesh, ITexture } from '../../../type/modelType'
import { textureToMeshConfiguration } from '../../../utils/modelEngine/configuration'
import {
  deleteProductVariant,
  upsertProductVariant,
} from '../../../utils/modelEngine/product'

interface IProps {
  shop: Shop
  mesh: IMesh
  setMesh: React.Dispatch<React.SetStateAction<IMesh | null>>
}

const TextureConfig: FunctionComponent<IProps> = ({ shop, mesh, setMesh }) => {
  const [textures, setTextures] = useState<ITexture[]>(txs)
  const [allow, setAllow] = useState<boolean>(
    !!mesh.textures?.some(t => t.isOption)
  )

  const textureVariantId = shop.variants.edges.find(
    e => e.node.no === '3d-textures-library'
  )?.node.id

  useEffect(() => {
    if (mesh) {
      setAllow(!!mesh.textures?.some(t => t.isOption))
    }
  }, [mesh])

  return (
    <VStack spacing="4" alignItems="left">
      <Box>
        <Switch
          size="lg"
          colorScheme="brand"
          onChange={e => {
            setAllow(e.target.checked)
            if (!e.target.checked) {
              if (textureVariantId && mesh.productId) {
                deleteProductVariant(textureVariantId, mesh.productId)
              }

              const textureId = mesh.textures?.find(c => c.default)?.id
              if (textureId) {
                const texture = textures.find(c => c.id === textureId)
                if (texture) {
                  setMesh({
                    ...mesh,
                    textures: [{ ...texture, default: true }],
                  })
                }
              }
            }
          }}
          isChecked={allow}
        />{' '}
        Allow user to change
      </Box>

      {allow && (
        <>
          <Input
            placeholder="Search Materials"
            onChange={e => {
              const q = e.target.value
              const patt = new RegExp(`.*${q}.*`, 'i')
              setTextures(txs.filter(t => patt.test(t.title)))
            }}
          />

          <VStack spacing="2">
            {textures.map(texture => {
              const textureConfig =
                mesh.textures?.find(t => t.id === texture.id) ?? texture
              return (
                <HStack w="100%">
                  <HStack spacing="2">
                    <Image
                      boxSize="42px"
                      objectFit="cover"
                      src={texture.source.texture}
                      alt="texture.title"
                    />
                    <Text>{texture.title}</Text>
                  </HStack>

                  <Spacer />
                  <Switch
                    size="md"
                    colorScheme="brand"
                    isChecked={!!textureConfig.isOption}
                    onChange={e => {
                      const newMeshConfig = textureToMeshConfiguration(
                        { ...textureConfig, isOption: e.target.checked },
                        mesh
                      )

                      if (textureVariantId && mesh.productId) {
                        if (!newMeshConfig.textures?.some(c => c.isOption)) {
                          deleteProductVariant(textureVariantId, mesh.productId)
                        } else {
                          upsertProductVariant(
                            textureVariantId,
                            newMeshConfig.textures.filter(v => v.isOption),
                            mesh.productId
                          )
                        }
                      }

                      setMesh(newMeshConfig)
                    }}
                  />
                  <Checkbox
                    colorScheme="brand"
                    isChecked={!!textureConfig.default}
                    onChange={e => {
                      const newMeshConfig = textureToMeshConfiguration(
                        { ...textureConfig, default: e.target.checked },
                        mesh
                      )

                      setMesh(newMeshConfig)
                    }}
                  ></Checkbox>
                </HStack>
              )
            })}
          </VStack>
        </>
      )}

      {!allow && (
        <Select
          placeholder="Select Default Material"
          onChange={e => {
            const textureConfig = textures.find(
              t => t.id === e.target.value
            ) as ITexture
            const newMeshConfig = textureToMeshConfiguration(
              { ...textureConfig, default: true },
              mesh
            )
            setMesh(newMeshConfig)
          }}
          value={mesh.textures?.find(t => t.default)?.id ?? ''}
        >
          {textures.map(texture => (
            <option value={texture.id}>{texture.title}</option>
          ))}
        </Select>
      )}
    </VStack>
  )
}

export default TextureConfig
